/**
 * ============================================================================
 * EXPORT MENU — "⋮" dropdown with Export to CSV / Export to Excel.
 * ============================================================================
 *
 * Drop into SectionCard's `actions` slot next to any table:
 *
 *   <SectionCard
 *     title="Metric Definitions"
 *     actions={<ExportMenu columns={exportCols} rows={rows} filename="metric-definitions" />}
 *   >
 *     <DataTable ... />
 *   </SectionCard>
 *
 * Columns use the ExportColumn contract (raw text per cell) — see
 * exportTable.ts for why this differs from DataTableColumn.
 */

import React, { useEffect, useState } from 'react';
import { ExportColumn, exportToCsv, exportToExcel } from './exportTable';
import './ExportMenu.scss';

interface ExportMenuProps<T> {
  columns: Array<ExportColumn<T>>;
  rows: T[];
  /** Download filename without extension, e.g. "metric-definitions". */
  filename: string;
}

export function ExportMenu<T>({ columns, rows, filename }: ExportMenuProps<T>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const handleExcel = async () => {
    setBusy(true);
    try {
      await exportToExcel(columns, rows, filename);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="export-menu" onClick={(e) => e.stopPropagation()}>
      <button
        className="export-menu__trigger"
        aria-label="Menu options"
        title="Menu options"
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open && (
        <div className="export-menu__dropdown">
          <button onClick={() => { exportToCsv(columns, rows, filename); setOpen(false); }}>
            Export to CSV
          </button>
          <button disabled={busy} onClick={handleExcel}>
            {busy ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>
      )}
    </div>
  );
}
